/**
 * Mains au tirage / après écart / début d'un pli — aligné sur le drill benchmark ML.
 */

import { getCardInfo, getCardShortName } from './cards';
import type { CardNumber } from '../types/game';
import { Contract } from '../types/game';
import type { DiffereStatsDealTrick } from './differeStatsReplay';

export const DEAL_SEAT_LABELS = ['Sud', 'Est', 'Nord', 'Ouest'] as const;

export interface DealHandsPanelInput {
  contract: number;
  takerPosition: number;
  initialHands: Record<string, number[]>;
  chien: number[];
  ecart: number[];
  tricks: DiffereStatsDealTrick[];
}

function sortCardIds(cards: number[]): number[] {
  return [...cards].sort((a, b) => a - b);
}

function multisetRemoveFromHand(hand: number[], toRemove: number[]): number[] {
  const rm = new Map<number, number>();
  for (const c of toRemove) rm.set(c, (rm.get(c) ?? 0) + 1);
  const out: number[] = [];
  for (const c of hand) {
    const n = rm.get(c) ?? 0;
    if (n > 0) rm.set(c, n - 1);
    else out.push(c);
  }
  return out;
}

function takerIntegratesChienAndEcart(contract: number): boolean {
  return contract !== Contract.GardeSans && contract !== Contract.GardeContre;
}

/** Mains au tirage (18 cartes par joueur, avant chien / écart). */
export function handsInitialDealFromDetail(detail: DealHandsPanelInput): number[][] | null {
  const initial = detail.initialHands;
  if (!initial || typeof initial !== 'object') return null;
  return Array.from({ length: 4 }, (_, i) =>
    sortCardIds([...(initial[String(i)] ?? [])]),
  );
}

/** Mains après révélation du chien et écart — avant le 1er pli. */
export function handsAfterEcartFromDetail(detail: DealHandsPanelInput): number[][] | null {
  const initial = detail.initialHands;
  if (!initial || typeof initial !== 'object') return null;
  const chien = detail.chien ?? [];
  const ecart = detail.ecart ?? [];
  const taker = ((detail.takerPosition % 4) + 4) % 4;
  const withChien = takerIntegratesChienAndEcart(detail.contract);
  return Array.from({ length: 4 }, (_, i) => {
    let h = [...(initial[String(i)] ?? [])];
    if (i === taker && withChien) {
      h = [...h, ...chien];
      h = multisetRemoveFromHand(h, ecart);
    }
    return sortCardIds(h);
  });
}

function playedCardsBySeatBeforeTrick(
  tricks: DiffereStatsDealTrick[],
  beforeTrickNum1: number,
): number[][] {
  const bySeat: number[][] = [[], [], [], []];
  const rows = [...tricks].sort((a, b) => a.trickIndex - b.trickIndex);
  for (const tr of rows) {
    const trickNum1 = tr.trickIndex + 1;
    if (trickNum1 >= beforeTrickNum1) break;
    for (let p = 0; p < tr.cards.length && p < tr.players.length; p++) {
      const seat = ((tr.players[p]! % 4) + 4) % 4;
      bySeat[seat]!.push(tr.cards[p]!);
    }
  }
  return bySeat;
}

/**
 * Mains au début du pli `focusTrickNum1` (1 = après écart).
 * `null` = tirage initial.
 */
export function handsAtStartOfTrick(
  detail: DealHandsPanelInput,
  focusTrickNum1: number | null,
): number[][] | null {
  if (focusTrickNum1 == null) return handsInitialDealFromDetail(detail);
  const base = handsAfterEcartFromDetail(detail);
  if (!base) return null;
  if (focusTrickNum1 <= 1) return base;
  const played = playedCardsBySeatBeforeTrick(detail.tricks, focusTrickNum1);
  return base.map((hand, seat) =>
    sortCardIds(multisetRemoveFromHand(hand, played[seat]!)),
  );
}

export function formatDealCardRowShort(cards: number[] | undefined | null): string {
  if (!cards?.length) return '—';
  const sorted = sortCardIds(cards.filter(c => c >= 1 && c <= 78));
  if (sorted.length === 0) return '—';
  return sorted.map(c => getCardShortName(c as CardNumber)).join('\t');
}

const HAND_GROUP_SUIT_SYM = ['♥', '♠', '♦', '♣'] as const;

function rankPlainColorCard(card: CardNumber): string {
  const info = getCardInfo(card);
  if (info.isExcuse || info.isTrump) return '?';
  const v = info.value;
  if (v === 1) return '1';
  if (v === 11) return 'V';
  if (v === 12) return 'C';
  if (v === 13) return 'D';
  if (v === 14) return 'R';
  return String(v);
}

/** Affichage mains groupées : ♥ … ♠ … ♦ … ♣ … puis atouts. */
export function renderDealHandGroupedSegments(hand: number[]): string[] {
  const valid = hand.filter(c => c >= 1 && c <= 78);
  if (valid.length === 0) return [];
  const suits: number[][] = [[], [], [], []];
  const trumpBucket: number[] = [];
  for (const c of valid) {
    if (c === 78 || (c >= 57 && c <= 77)) {
      trumpBucket.push(c);
      continue;
    }
    if (c >= 1 && c <= 14) suits[0]!.push(c);
    else if (c >= 15 && c <= 28) suits[1]!.push(c);
    else if (c >= 29 && c <= 42) suits[2]!.push(c);
    else if (c >= 43 && c <= 56) suits[3]!.push(c);
  }
  const segments: string[] = [];
  for (let s = 0; s < 4; s++) {
    sortCardIds(suits[s]!);
    if (suits[s]!.length === 0) continue;
    segments.push(`${HAND_GROUP_SUIT_SYM[s]} ${suits[s]!.map(c => rankPlainColorCard(c as CardNumber)).join(',')}`);
  }
  const trumps = sortCardIds(trumpBucket.filter(c => c >= 57 && c <= 77));
  const trumpLabels: string[] = trumps.map(c => String(c - 56));
  if (trumpBucket.includes(78)) trumpLabels.push('EXC');
  if (trumpLabels.length > 0) segments.push(`A ${trumpLabels.join(',')}`);
  return segments;
}
